import { useEffect } from 'react';

import './DetailDrawer.css';

interface DetailDrawerProps {
  /** Accessible name — shown as `<h2>` at the top of the drawer. */
  title: string;
  /** Smaller supporting copy rendered under the title. */
  subtitle?: string;
  /**
   * Arbitrary object rendered as pretty-printed JSON. This is a
   * vendor-verbatim inspection view — no editing, no follow-up flows.
   */
  payload: unknown;
  /** Close handler — fired by the close button and by the ESC key. */
  onClose: () => void;
  /** `data-testid` root. Defaults to `detail-drawer`. */
  testId?: string;
}

/**
 * DetailDrawer — shared right-side inspection pane used by the replays,
 * divergence, attestations, quorum-keys, integrations, coverage and
 * dashboard-recent-runs screens to open a row's vendor-verbatim JSON.
 *
 * Intentionally minimal: no nested editors, no form fields. Anything a row
 * provides gets JSON-stringified and shown inside a `<pre>`. ESC closes.
 */
export default function DetailDrawer({
  title,
  subtitle,
  payload,
  onClose,
  testId = 'detail-drawer',
}: DetailDrawerProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <aside
      className="detail-drawer"
      role="complementary"
      aria-label={title}
      data-testid={testId}
    >
      <header className="detail-drawer__head">
        <div>
          <div className="eyebrow">§ DETAIL</div>
          <h2 className="serif detail-drawer__title">{title}</h2>
          {subtitle && <p className="detail-drawer__sub">{subtitle}</p>}
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          data-testid={`${testId}-close`}
        >
          Close
        </button>
      </header>

      <section className="detail-drawer__section">
        <div className="eyebrow">VENDOR PAYLOAD (VERBATIM)</div>
        <pre
          className="detail-drawer__raw mono"
          data-testid={`${testId}-payload`}
        >
          {stringify(payload)}
        </pre>
      </section>
    </aside>
  );
}

function stringify(value: unknown): string {
  try {
    return JSON.stringify(value, (_key, v) => (typeof v === 'bigint' ? v.toString() : v), 2);
  } catch {
    return String(value);
  }
}
